import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { Badge, Card, Row, Col, Button, ButtonGroup, Spinner, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getMovements, getInitialDeployments, getActivitySummary } from '../../../../../../services/apiaryService';
import moment from 'moment';
import GenericExportCSVModal from '../../../shared/GenericExportCSVModal';

const { SearchBar } = Search;

// --- Styled Components ---

const DashboardContainer = styled.div`
    padding: 15px 0;
`;

const BatchInfo = styled.div`
    font-size: 11px;
    color: #b2bec3;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    align-items: center;
    padding: 0 5px;
`;

const StatCard = styled(Card)`
    border: none;
    border-radius: 15px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    margin-bottom: 25px;
    transition: transform 0.2s;
    background: ${props => props.bg || '#fff'};
    color: ${props => props.color || '#2d3436'};
    overflow: hidden;

    &:hover {
        transform: translateY(-5px);
    }
`;

const CardHeaderDecoration = styled.div`
    height: 4px;
    background: ${props => props.accent || '#ff8701'};
    width: 100%;
`;

const StatTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;
    margin-bottom: 4px;
`;

const StatSubTitle = styled.div`
    font-size: 13px;
    font-weight: 500;
    opacity: 0.6;
    margin-bottom: 12px;
`;

const StatValue = styled.div`
    font-size: 32px;
    font-weight: 800;
    display: flex;
    align-items: baseline;
    gap: 8px;
`;

const ViewToggleContainer = styled.div`
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.03);

    .react-bootstrap-table {
        border: none;
    }

    th {
        background: #fdfdfd;
        border: none !important;
        border-bottom: 2px solid #f1f2f6 !important;
        color: #2d3436;
        font-size: 14px;
        font-weight: 900;
        text-transform: uppercase;
        padding: 15px !important;
        letter-spacing: 1px;
    }

    td {
        border-color: #f8f9fa !important;
        vertical-align: middle !important;
        padding: 15px !important;
        font-size: 15px;
        color: #2d3436;
    }

    tr:hover {
        background-color: #fdfdfd;
    }
`;

const MovementType = styled.span`
    font-weight: 800;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${props => props.isIncoming ? '#00b894' : '#d63031'};
`;

// --- Helper Functions ---

const safeFormatDate = (date) => {
    if (!date) return 'N/A';
    try {
        const m = moment(date);
        return m.isValid() ? m.format('DD MMM YYYY') : 'Invalid Date';
    } catch (e) {
        return 'N/A';
    }
};

const PURPOSE_COLORS = {
    MIGRATION: 'primary',
    STORAGE: 'info',
    REDISTRIBUTION: 'warning',
    RETURN_TO_FACTORY: 'secondary',
    DEPLOYMENT: 'success',
};

const getPurposeBadge = (purpose) => {
    const p = String(purpose || '').toUpperCase();
    const variant = PURPOSE_COLORS[p] || 'secondary';
    
    return (
        <Badge 
            pill 
            variant={variant} 
            style={{ 
                fontSize: '13px', 
                padding: '8px 15px', 
                fontWeight: '800',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
            }}
        >
            {p || 'GENERAL'}
        </Badge>
    );
};

const NectarHiveMovements = ({ apiaryId }) => {
    const [view, setView] = useState('movements'); // 'movements' | 'deployments'
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);       // from /activity-summary
    const [movements, setMovements] = useState([]);     // from /movements
    const [deployments, setDeployments] = useState([]); // from /deployments
    const [purposeFilter, setPurposeFilter] = useState('ALL');
    const [showExportModal, setShowExportModal] = useState(false);

    // ──────────────────────────────────────────────
    // Step 1: Fetch all 3 APIs in parallel on mount
    // ──────────────────────────────────────────────
    const fetchData = async () => {
        setLoading(true);
        try {
            const [summaryRes, deploymentsRes, movementsRes] = await Promise.all([
                getActivitySummary(apiaryId),
                getInitialDeployments(apiaryId),
                getMovements(apiaryId),
            ]);

            setSummary(summaryRes || null);
            setDeployments(Array.isArray(deploymentsRes) ? deploymentsRes : []);
            setMovements(Array.isArray(movementsRes) ? movementsRes : []);
        } catch (error) {
            console.error("❌ Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (apiaryId) fetchData();
    }, [apiaryId]);

    // ──────────────────────────────────────────────
    // Derived Data (purposes for filter dropdown)
    // ──────────────────────────────────────────────
    const purposes = useMemo(() => {
        const set = new Set(['ALL']);
        movements.forEach(m => { if (m.purpose) set.add(m.purpose); });
        return Array.from(set);
    }, [movements]);

    const filteredMovements = useMemo(() => {
        if (purposeFilter === 'ALL') return movements;
        return movements.filter(m => m.purpose === purposeFilter);
    }, [movements, purposeFilter]);

    // ──────────────────────────────────────────────
    // Step 5: History Table — Movement Columns
    // ──────────────────────────────────────────────
    const movementColumns = [
        {
            dataField: 'movement_date',
            text: 'Date',
            sort: true,
            formatter: (cell, row) => (
                <div style={{ color: '#636e72', fontWeight: 500 }}>
                    {safeFormatDate(cell || row.created_at)}
                </div>
            )
        },
        {
            dataField: 'hives_moved',
            text: 'Hives',
            formatter: (cell, row) => {
                const currentId = apiaryId.toUpperCase();
                const isIncoming = (row.to_apiary_id || '').toUpperCase() === currentId;
                const count = row.hives_moved || row.hive_count || 0;
                return (
                    <MovementType isIncoming={isIncoming}>
                        {isIncoming ? '+' : '-'}{count}
                    </MovementType>
                );
            }
        },
        {
            dataField: 'other_apiary',
            text: 'New Apiary',
            isDummyField: true,
            formatter: (cell, row) => {
                // Show the OTHER apiary (not the current page)
                const currentId = apiaryId.toUpperCase();
                const isIncoming = (row.to_apiary_id || '').toUpperCase() === currentId;

                let linkName, linkId;
                if (isIncoming) {
                    // Hives came FROM another apiary
                    linkName = row.from_apiary_name || row.from_apiary_id || 'Factory';
                    linkId = row.from_apiary_id;
                } else {
                    // Hives went TO another apiary
                    linkName = row.to_apiary_name || row.to_apiary_id || 'Factory';
                    linkId = row.to_apiary_id;
                }

                return linkId ? (
                    <Link to={`/nector-apiaries/${linkId}`} style={{ fontWeight: 'bold', color: '#ff8701' }}>
                        {linkName}
                    </Link>
                ) : (
                    <span style={{ fontWeight: 'bold', color: '#636e72' }}>{linkName}</span>
                );
            }
        },
        {
            dataField: 'to_apiary_flora',
            text: 'Flora',
            formatter: (cell) => {
                if (!cell) return <span style={{ color: '#b2bec3' }}>—</span>;
                
                const floraType = cell.type || '—';
                // Backend returns `flower` array per the guide
                const flowers = Array.isArray(cell.flower)
                    ? cell.flower.map(f => f.plant).filter(Boolean).join(', ')
                    : (Array.isArray(cell.selected_flower) 
                        ? cell.selected_flower.map(f => f.plant).filter(Boolean).join(', ') 
                        : (cell.selected_flower?.plant || ''));

                const isMulti = floraType === 'Multifloral';
                return (
                    <div style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Badge
                            pill
                            variant={isMulti ? 'primary' : 'success'}
                            style={{ 
                                fontSize: '10px', 
                                padding: '4px 10px',
                                fontWeight: 700 
                            }}
                        >
                            {floraType}
                        </Badge>
                        {flowers && <span style={{ fontWeight: 500 }}>{flowers}</span>}
                    </div>
                );
            }
        },
        {
            dataField: 'purpose',
            text: 'Purpose',
            formatter: (cell) => getPurposeBadge(cell)
        }
    ];

    // ──────────────────────────────────────────────
    // Step 6: Initial Table — Deployment Columns
    // ──────────────────────────────────────────────
    const deploymentColumns = [
        {
            dataField: 'deployed_on',
            text: 'Date',
            sort: true,
            formatter: (cell, row) => (
                <div style={{ color: '#636e72', fontWeight: 600 }}>
                    {safeFormatDate(cell || row.created_at)}
                </div>
            )
        },
        {
            dataField: 'batch_id',
            text: 'Batch',
            formatter: (cell) => (
                <span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '12px' }}>
                    {cell || '—'}
                </span>
            )
        },
        {
            dataField: 'hives_deployed',
            text: 'Hives',
            sort: true,
            formatter: (cell) => <span style={{ color: '#00b894', fontWeight: 700, fontSize: '15px' }}>+{cell || 0}</span>
        },
        {
            dataField: 'status',
            text: 'Status',
            formatter: (cell) => (
                <Badge 
                    variant={cell === 'ACTIVE' ? 'success' : 'danger'} 
                    style={{ fontSize: '11px', padding: '5px 12px', fontWeight: 700 }}
                >
                    {cell || 'UNKNOWN'}
                </Badge>
            )
        },
        {
            dataField: 'revoked_at',
            text: 'Revoked At',
            formatter: (cell) => cell ? (
                <span style={{ color: '#d63031', fontWeight: 500 }}>{safeFormatDate(cell)}</span>
            ) : (
                <span style={{ color: '#b2bec3' }}>—</span>
            )
        }
    ];

    const movementExportColumns = [
        { label: 'Date', key: 'date', formatter: (row) => safeFormatDate(row.movement_date || row.created_at) },
        { label: 'Hives Moved', key: 'hives', formatter: (row) => row.hives_moved || row.hive_count || 0 },
        {
            label: 'Other Apiary',
            key: 'other_apiary',
            formatter: (row) => {
                const currentId = (apiaryId || '').toUpperCase();
                const isIncoming = (row.to_apiary_id || '').toUpperCase() === currentId;
                return isIncoming
                    ? (row.from_apiary_name || row.from_apiary_id || 'Factory')
                    : (row.to_apiary_name || row.to_apiary_id || 'Factory');
            }
        },
        { label: 'Purpose', key: 'purpose', formatter: (row) => row.purpose || 'GENERAL' }
    ];

    const deploymentExportColumns = [
        { label: 'Date', key: 'date', formatter: (row) => safeFormatDate(row.deployed_on || row.created_at) },
        { label: 'Batch', key: 'batch_id', formatter: (row) => row.batch_id || '' },
        { label: 'Hives Deployed', key: 'hives_deployed', formatter: (row) => row.hives_deployed || 0 },
        { label: 'Status', key: 'status', formatter: (row) => row.status || 'UNKNOWN' },
        { label: 'Revoked At', key: 'revoked_at', formatter: (row) => row.revoked_at ? safeFormatDate(row.revoked_at) : '' }
    ];

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" variant="primary" size="lg" />
                <p className="mt-3 text-muted" style={{ fontSize: '15px' }}>Analyzing hive logistics...</p>
            </div>
        );
    }

    // ──────────────────────────────────────────────
    // Step 2 & 3: Render — Batches + Summary Cards
    // All values come directly from summary (backend)
    // ──────────────────────────────────────────────
    const batchesInvolved = summary?.batches_involved || [];
    const currentHiveStock = summary?.current_hive_stock ?? 0;
    const totalStocked = summary?.total_stocked ?? 0;
    const totalShippedOut = summary?.total_shipped_out ?? 0;

    // "Stocked from factory" = sum of ACTIVE deployment hives (what actually came from the factory)
    // Use the higher of backend initial_stock or our deployment-based sum
    const factoryStockFromDeployments = deployments
        .filter(d => (d.status || '').toUpperCase() === 'ACTIVE')
        .reduce((acc, d) => acc + Number(d.hives_deployed || 0), 0);
    const factoryStock = Math.max(summary?.initial_stock ?? 0, factoryStockFromDeployments);

    return (
        <DashboardContainer>
            {/* --- Step 2: Batches Involved Tags --- */}
            {batchesInvolved.length > 0 && (
                <BatchInfo>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#2d3436' }}>
                        <i className="fa fa-layer-group mr-1 text-primary"></i> BATCHES INVOLVED:
                    </span>
                    {batchesInvolved.map(b => (
                        <Badge 
                            key={b} 
                            variant="primary" 
                            style={{ 
                                fontWeight: 600, 
                                padding: '5px 12px', 
                                borderRadius: '15px',
                                fontSize: '11px',
                                background: '#eef2ff',
                                color: '#2d3436'
                            }}
                        >
                            {b}
                        </Badge>
                    ))}
                </BatchInfo>
            )}

            {/* --- Step 3: Three Summary Cards (all from backend) --- */}
            <Row>
                <Col md={4}>
                    <StatCard accent="#ff8701">
                        <CardHeaderDecoration accent="#ff8701" />
                        <Card.Body>
                            <StatTitle>Current Hive Stock</StatTitle>
                            <StatSubTitle>Stocked from factory: {factoryStock}</StatSubTitle>
                            <StatValue>
                                {currentHiveStock}
                                <span style={{ fontSize: '14px', color: '#b2bec3', fontWeight: 400 }}>Hives Total</span>
                            </StatValue>
                        </Card.Body>
                    </StatCard>
                </Col>
                <Col md={4}>
                    <StatCard accent="#00b894">
                        <CardHeaderDecoration accent="#00b894" />
                        <Card.Body>
                            <StatTitle>Total Stocked</StatTitle>
                            <StatSubTitle>Total hives added to apiary</StatSubTitle>
                            <StatValue color="#00b894">
                                {totalStocked}
                                <i className="fa fa-caret-up text-success ml-2" style={{ fontSize: '18px' }}></i>
                            </StatValue>
                        </Card.Body>
                    </StatCard>
                </Col>
                <Col md={4}>
                    <StatCard accent="#d63031">
                        <CardHeaderDecoration accent="#d63031" />
                        <Card.Body>
                            <StatTitle>Total Shipped Out</StatTitle>
                            <StatSubTitle>Total hives moved out</StatSubTitle>
                            <StatValue color="#d63031">
                                {totalShippedOut}
                                <i className="fa fa-caret-down text-danger ml-2" style={{ fontSize: '18px' }}></i>
                            </StatValue>
                        </Card.Body>
                    </StatCard>
                </Col>
            </Row>

            {/* --- Step 4-7: History / Initial Tabs + Table --- */}
            <ToolkitProvider
                keyField="id"
                data={view === 'movements' ? filteredMovements : deployments}
                columns={view === 'movements' ? movementColumns : deploymentColumns}
                search
            >
                {
                    props => (
                        <React.Fragment>
                            <ViewToggleContainer>
                                <div className="d-flex align-items-center gap-3">
                                    <ButtonGroup size="sm">
                                        <Button 
                                            variant={view === 'movements' ? 'primary' : 'outline-primary'} 
                                            style={{ borderRadius: '20px 0 0 20px', padding: '8px 20px' }}
                                            onClick={() => setView('movements')}
                                        >
                                            History ({movements.length})
                                        </Button>
                                        <Button 
                                            variant={view === 'deployments' ? 'primary' : 'outline-primary'} 
                                            style={{ borderRadius: '0 20px 20px 0', padding: '8px 20px' }}
                                            onClick={() => setView('deployments')}
                                        >
                                            Initial ({deployments.length})
                                        </Button>
                                    </ButtonGroup>

                                    {view === 'movements' && (
                                        <Form.Control 
                                            as="select"
                                            size="sm" 
                                            style={{ width: '180px', borderRadius: '20px', fontSize: '12px' }}
                                            value={purposeFilter}
                                            onChange={(e) => setPurposeFilter(e.target.value)}
                                        >
                                            {purposes.map(p => <option key={p} value={p}>{p}</option>)}
                                        </Form.Control>
                                    )}
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <SearchBar
                                        {...props.searchProps}
                                        placeholder="Search logs..."
                                        style={{ borderRadius: '20px', padding: '8px 25px', width: '250px' }}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setShowExportModal(true)}
                                        style={{ fontWeight: 700, borderRadius: '20px' }}
                                    >
                                        <i className="fa fa-download mr-2"></i> Export CSV
                                    </Button>
                                </div>
                            </ViewToggleContainer>

                            <TableWrapper>
                                <BootstrapTable
                                    {...props.baseProps}
                                    bordered={false}
                                    hover
                                    noDataIndication={() => (
                                        <div className="text-center p-5 text-muted">
                                            <i className="fa fa-folder-open mb-3 d-block" style={{ fontSize: '30px', opacity: 0.3 }}></i>
                                            No {view === 'movements' ? 'movement' : 'deployment'} records found for this apiary.
                                        </div>
                                    )}
                                    pagination={paginationFactory({
                                        sizePerPage: 10,
                                        hideSizePerPage: true,
                                        hidePageListOnlyOnePage: true
                                    })}
                                />
                            </TableWrapper>
                        </React.Fragment>
                    )
                }
            </ToolkitProvider>

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={view === 'movements' ? filteredMovements : deployments}
                columns={view === 'movements' ? movementExportColumns : deploymentExportColumns}
                title={`Export ${view === 'movements' ? 'Hive Movements' : 'Initial Deployments'} to CSV`}
                filenamePrefix={view === 'movements' ? 'hive_movements_export' : 'initial_deployments_export'}
            />
        </DashboardContainer>
    );
};

export default NectarHiveMovements;
